/**
 * SA 主導の報酬請求書 (commission_invoices) hooks (v3.2.14 / Phase 3a + 3b)。
 *
 * 物理コレクション: commission_invoices/{invoiceId}
 *
 * 提供:
 *   - useCommissionInvoices(): 全件 (管理者向け)
 *   - useMyCommissionInvoices(uid): 自分の請求書 (SA 向け)
 *   - useCommissionInvoiceByToken(token): 公開URL用 (経理閲覧用、認証不要は別途 functions HTTP)
 *   - useMyUnbilledBriefings(uid): 未請求の個別説明代行料一覧 (v3.4)
 *   - useCreateCommissionInvoice / useMarkCommissionInvoicePaid /
 *     useCancelCommissionInvoice / useSendCommissionInvoiceReminder
 *   - invoiceLineBreakdown(inv): 明細の kind 別内訳 (報酬 n 件 / 個別説明 m 件)
 */
import type { CommissionInvoice } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface CommissionInvoiceWithId extends CommissionInvoice {
  id: string;
}

/** 明細の kind 別内訳。briefing_fee 行は quantity (件数) で数える。 */
export function invoiceLineBreakdown(inv: CommissionInvoice): {
  salesCount: number;
  briefingCount: number;
} {
  let salesCount = 0;
  let briefingCount = 0;
  for (const li of inv.lineItems) {
    if (li.kind === 'briefing_fee') briefingCount += li.quantity ?? 0;
    else salesCount += 1;
  }
  return { salesCount, briefingCount };
}

/** 全件取得 (管理者用)。 */
export function useCommissionInvoices() {
  return useQuery({
    queryKey: ['commission_invoices', 'all'],
    queryFn: async (): Promise<CommissionInvoiceWithId[]> => {
      const snap = await getDocs(collection(db, 'commission_invoices'));
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as CommissionInvoice),
      }));
      list.sort((a, b) => {
        const at =
          typeof a.issuedAt === 'object' && 'toMillis' in a.issuedAt
            ? a.issuedAt.toMillis()
            : new Date(a.issuedAt as string).getTime();
        const bt =
          typeof b.issuedAt === 'object' && 'toMillis' in b.issuedAt
            ? b.issuedAt.toMillis()
            : new Date(b.issuedAt as string).getTime();
        return bt - at;
      });
      return list;
    },
  });
}

/** 自分の請求書 (SA 用)。 */
export function useMyCommissionInvoices(uid: string | null | undefined) {
  return useQuery({
    queryKey: ['commission_invoices', 'mine', uid],
    enabled: !!uid,
    queryFn: async (): Promise<CommissionInvoiceWithId[]> => {
      if (!uid) return [];
      const snap = await getDocs(
        query(collection(db, 'commission_invoices'), where('recipientId', '==', uid)),
      );
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as CommissionInvoice),
      }));
      list.sort((a, b) => {
        const at =
          typeof a.issuedAt === 'object' && 'toMillis' in a.issuedAt
            ? a.issuedAt.toMillis()
            : new Date(a.issuedAt as string).getTime();
        const bt =
          typeof b.issuedAt === 'object' && 'toMillis' in b.issuedAt
            ? b.issuedAt.toMillis()
            : new Date(b.issuedAt as string).getTime();
        return bt - at;
      });
      return list;
    },
  });
}

/** publicToken で公開閲覧。Firestore Rules では recipientId == auth.uid または editor+ のみ許可しているため、
 * 経理が外部から閲覧する場合は別途 HTTP Function を経由する必要がある (TODO)。
 * 現状は社内認証ありの閲覧用。 */
export function useCommissionInvoiceByToken(token: string | null | undefined) {
  return useQuery({
    queryKey: ['commission_invoices', 'by_token', token],
    enabled: !!token,
    queryFn: async (): Promise<CommissionInvoiceWithId | null> => {
      if (!token) return null;
      const snap = await getDocs(
        query(collection(db, 'commission_invoices'), where('publicToken', '==', token)),
      );
      if (snap.empty) return null;
      const d = snap.docs[0];
      return { id: d.id, ...(d.data() as CommissionInvoice) };
    },
  });
}

// ─── 未請求 個別説明代行料 (v3.4) ───

export interface UnbilledBriefingItem {
  dealId: string;
  recordId: string;
  /** 実施日 (ISO)。旧データ欠損時は null */
  conductedAt: string | null;
  dealTitle: string;
  contactName: string;
}

const listUnbilledBriefingsFn = httpsCallable<
  { saId?: string },
  { ok: true; briefingFee: number; items: UnbilledBriefingItem[] }
>(functions, 'listMyUnbilledBriefings');

/** 自分の未請求 個別説明代行料 (単価 + 件数一覧)。
 * rules 上 SA は briefing_records を直接 read できないため Callable 経由。 */
export function useMyUnbilledBriefings(uid: string | null | undefined) {
  return useQuery({
    queryKey: ['unbilled_briefings', uid],
    enabled: !!uid,
    queryFn: async () => {
      const r = await listUnbilledBriefingsFn({});
      return r.data;
    },
  });
}

// ─── Mutations ───

const createFn = httpsCallable<
  {
    recordPaths: string[];
    saId?: string;
    briefingRecordIds?: Array<{ dealId: string; recordId: string }>;
    periodLabel?: string | null;
    memo?: string | null;
  },
  {
    ok: true;
    invoiceId: string;
    invoiceNumber: string;
    publicToken: string;
    publicUrl: string;
    mailSent: boolean;
    mailReason?: string;
  }
>(functions, 'createCommissionInvoice');

export function useCreateCommissionInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof createFn>[0]) => {
      const r = await createFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission_invoices'] });
      qc.invalidateQueries({ queryKey: ['commission_records'] });
      qc.invalidateQueries({ queryKey: ['unbilled_briefings'] });
      qc.invalidateQueries({ queryKey: ['briefing_records', 'all'] });
    },
  });
}

const markPaidFn = httpsCallable<
  { invoiceId: string; paidAt?: string | null },
  { ok: true; recordCount: number; mailSent: boolean; mailReason?: string }
>(functions, 'markCommissionInvoicePaid');

export function useMarkCommissionInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof markPaidFn>[0]) => {
      const r = await markPaidFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission_invoices'] });
      qc.invalidateQueries({ queryKey: ['commission_records'] });
    },
  });
}

const cancelFn = httpsCallable<{ invoiceId: string }, { ok: true }>(
  functions,
  'cancelCommissionInvoice',
);

export function useCancelCommissionInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: Parameters<typeof cancelFn>[0]) => {
      const r = await cancelFn(input);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission_invoices'] });
      qc.invalidateQueries({ queryKey: ['commission_records'] });
      qc.invalidateQueries({ queryKey: ['unbilled_briefings'] });
      qc.invalidateQueries({ queryKey: ['briefing_records', 'all'] });
    },
  });
}

const reminderFn = httpsCallable<
  { recordPaths: string[] },
  { ok: true; mailSent: boolean; mailReason?: string }
>(functions, 'sendCommissionInvoiceReminder');

export function useSendCommissionInvoiceReminder() {
  return useMutation({
    mutationFn: async (input: Parameters<typeof reminderFn>[0]) => {
      const r = await reminderFn(input);
      return r.data;
    },
  });
}
